import React from "react";
import {
  FileText,
  Shield,
  AlertCircle,
  CheckCircle,
  Scale,
  Eye,
  Lock,
  Database,
  UserCheck,
  FileEdit,
  Trash2,
  RefreshCw,
  Ban,
  ExternalLink,
} from "lucide-react";
import { motion } from "framer-motion";
import PageLayout from "@/components/PageLayout";
import HeroSection from "@/components/HeroSection";
import Section from "@/components/Section";
import SectionHeader from "@/components/SectionHeader";
import { Button } from "@/components/Button";

// ============================================
// IMMAGINI
// ============================================

const HERO_IMAGE = "/images/99-castelli-header.webp";

// ============================================
// ANIMAZIONI
// ============================================

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

// ============================================
// DATI
// ============================================

const dirittiPrivacy = [
  {
    icon: Trash2,
    title: "Cancellazione",
    description: "Richiedere la cancellazione, la trasformazione in forma anonima o il blocco dei dati trattati in violazione di legge.",
  },
  {
    icon: RefreshCw,
    title: "Aggiornamento e Rettifica",
    description: "Ottenere l'aggiornamento, la rettifica ovvero, qualora vi sia interesse, l'integrazione dei propri dati personali.",
  },
  {
    icon: Ban,
    title: "Opposizione al Trattamento",
    description: "Opporsi, in tutto o in parte, per motivi legittimi, al trattamento dei dati personali, ancorché pertinenti allo scopo della raccolta.",
  },
  {
    icon: Shield,
    title: "Opposizione al Marketing",
    description: "Opporsi al trattamento dei dati personali previsto a fini di informazione commerciale o di invio di materiale pubblicitario.",
  },
];

// ============================================
// COMPONENTI
// ============================================

const Trasparenza: React.FC = () => {
  return (
    <PageLayout>
      {/* Hero Section */}
      <HeroSection
        title="Trasparenza"
        subtitle="Statuto, Condizioni e Informativa Privacy dell'ASD 99 Castelli"
        backgroundImage={HERO_IMAGE}
        height="medium"
      />

      {/* Statuto Section */}
      <Section background="white">
        <div className="max-w-4xl mx-auto">
          <SectionHeader
            title="Statuto"
            subtitle="Il documento fondamentale della nostra associazione"
          />
          
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="bg-surface rounded-xl p-8 shadow-card border border-border"
          >
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <FileText className="w-8 h-8 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-display font-semibold text-text mb-2">
                  Statuto ASD 99 Castelli
                </h3>
                <p className="text-text-light mb-4">
                  Leggi lo Statuto completo dell'Associazione Sportiva Dilettantistica 99 Castelli. 
                  Il documento contiene tutti i principi fondamentali, gli scopi e le regole che governano la nostra associazione.
                </p>
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => window.open("https://drive.google.com/file/d/1fB8dfw1zocss_2JVT2S-KF3iyOfC2l6L/view?usp=drive_link", "_blank")}
                  className="inline-flex items-center gap-2"
                >
                  <FileText size={18} />
                  Leggi lo Statuto
                  <ExternalLink size={16} />
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </Section>

      {/* Condizioni Section */}
      <Section background="gray">
        <div className="max-w-4xl mx-auto">
          <SectionHeader
            title="Condizioni"
            subtitle="Dichiarazioni e accettazioni per il tesseramento"
          />
          
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="bg-surface rounded-xl p-8 shadow-card border border-border mb-8"
          >
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-border">
              <AlertCircle className="w-6 h-6 text-primary" />
              <p className="text-lg font-semibold text-text">
                Firmando il modulo per nuovo/rinnovo tesseramento si ACCETTANO le seguenti condizioni DICHIARANDO:
              </p>
            </div>
            
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <CheckCircle size={20} className="text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-text-light">
                  Conoscere ed accettare quanto stabilito dallo Statuto di ASD 99 Castelli, disponibile sul sito{" "}
                  <a 
                    href="https://www.99castelli.it/documenti" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline font-medium"
                  >
                    www.99castelli.it/documenti
                  </a>.
                </span>
              </li>
              
              <li className="flex items-start gap-3">
                <CheckCircle size={20} className="text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-text-light">
                  Impegnarsi a versare la quota associativa annua comunicata sul sito e/o sulla{" "}
                  <a 
                    href="https://facebook.com/99castelli" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline font-medium"
                  >
                    pagina Facebook di ASD 99 Castelli
                  </a>{" "}
                  a seconda dell'attività scelta.
                </span>
              </li>
              
              <li className="flex items-start gap-3">
                <CheckCircle size={20} className="text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-text-light">
                  Essere a conoscenza che la quota associativa annuale rappresenta il contributo alle attività 
                  sociali ed è comprensiva di assicurazione di base per l'attività per cui ci si tessera 
                  (vedasi le{" "}
                  <a 
                    href="https://www.centrosportivoitaliano.it/servizi/assicurazione/assicurazioni" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline font-medium"
                  >
                    condizioni assicurative CSI
                  </a>
                  ), pertanto non è trasferibile e non viene rimborsata in caso di dimissioni.
                </span>
              </li>
              
              <li className="flex items-start gap-3">
                <CheckCircle size={20} className="text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-text-light">
                  Essere in possesso dell'idoneità psicofisica ed esente da malattie controindicate alla 
                  pratica delle attività previste dallo Statuto della ASD 99 Castelli e impegnarsi a 
                  consegnare copia del certificato medico all'Associazione, ove previsto.
                </span>
              </li>
              
              <li className="flex items-start gap-3">
                <CheckCircle size={20} className="text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-text-light">
                  Essere stato informato che con la presente si diventa socio di un'Associazione sportiva 
                  non a scopo di lucro.
                </span>
              </li>
              
              <li className="flex items-start gap-3">
                <CheckCircle size={20} className="text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-text-light">
                  Essere consapevole che partecipare a qualsiasi attività organizzata dalla ASD 99 Castelli 
                  e agli eventi sportivi in generale sono potenzialmente attività a rischio per la propria 
                  persona, per terzi, animali e cose.
                </span>
              </li>
            </ul>
          </motion.div>

          {/* Dichiarazione Rischi */}
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="bg-surface rounded-xl p-8 shadow-card border border-border mb-8"
          >
            <div className="flex items-center gap-3 mb-4">
              <Shield className="w-6 h-6 text-amber-500" />
              <h3 className="text-lg font-semibold text-text">Dichiarazione di Assunzione Rischi</h3>
            </div>
            <p className="text-text-light mb-4">
              Dichiaro inoltre di iscrivermi volontariamente e mi assumo tutti i rischi derivanti dalla mia partecipazione 
              a qualsiasi evento per eventuali cadute, contatti con veicoli, con altri partecipanti, spettatori o altri, 
              condizioni di tempo, incluso caldo torrido, freddo estremo e/o umido, traffico e condizione della strada, 
              condizione del fondo attraversato e ogni tipo di rischio ben conosciuto e da me valutato.
            </p>
            <p className="text-text-light">
              Essendo a conoscenza di quanto sopra, considerando l'accettazione della mia iscrizione/rinnovo, io, per mio 
              conto e nell'interesse di nessun altro, sollevo e libero l'ASD 99 Castelli ed il relativo Presidente, gli enti 
              promotori, tutti gli Sponsor, i rispettivi rappresentanti, successori, funzionari, direttori, membri, agenti 
              ed impiegati delle Società sopra citate, nonché i proprietari dei fondi privati attraversati durante lo 
              svolgimento delle attività, di tutti i presenti e futuri reclami o responsabilità di ogni tipo, conosciuti 
              o sconosciuti, derivanti dalla mia partecipazione alle attività organizzate.
            </p>
          </motion.div>

          {/* Disclaimer Responsabilità */}
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="bg-red-50 rounded-xl p-6 border border-red-200"
          >
            <div className="flex items-center gap-3 mb-2">
              <Scale className="w-6 h-6 text-red-600" />
              <h3 className="text-lg font-bold text-red-800">Importante</h3>
            </div>
            <p className="text-red-700 font-medium">
              La ASD 99 Castelli non è in alcun modo responsabile dal punto di vista penale e civile degli eventuali 
              danni causati dalla pratica delle attività statutarie.
            </p>
          </motion.div>
        </div>
      </Section>

      {/* Informativa Privacy Section */}
      <Section background="white">
        <div className="max-w-4xl mx-auto">
          <SectionHeader
            title="Informativa Privacy"
            subtitle="Ai sensi dell'art. 13 del Regolamento 2016/679/UE (GDPR)"
          />
          
          {/* Introduzione */}
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="bg-surface rounded-xl p-8 shadow-card border border-border mb-8"
          >
            <div className="flex items-center gap-3 mb-4">
              <Eye className="w-6 h-6 text-primary" />
              <h3 className="text-lg font-semibold text-text">Informativa sul Trattamento dei Dati</h3>
            </div>
            <p className="text-text-light">
              Ai sensi dell'art. 13 del Regolamento 2016/679/UE - Regolamento generale sulla protezione dei dati, 
              recante disposizioni sul nuovo "Codice in materia di protezione dei dati personali" si informa che i 
              dati forniti saranno trattati nel rispetto della normativa sopra richiamata e degli obblighi di riservatezza.
            </p>
          </motion.div>

          {/* Finalità e Piattaforma */}
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="bg-surface rounded-xl p-8 shadow-card border border-border mb-8"
          >
            <div className="flex items-center gap-3 mb-4">
              <Database className="w-6 h-6 text-primary" />
              <h3 className="text-lg font-semibold text-text">Finalità del Trattamento</h3>
            </div>
            <p className="text-text-light mb-4">
              Tali dati verranno trattati per finalità istituzionali, quindi strettamente connesse e strumentali 
              all'attività dell'Associazione ASD 99 Castelli e verranno caricati sulla piattaforma CSI - Centro 
              Sportivo Italiano, che ha una sua specifica INFORMATIVA PRIVACY (che si invita a leggere sul sito{" "}
              <a href="https://www.csi-net.it" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">
                www.csi-net.it
              </a>
              ), per concludere il tesseramento.
            </p>
            <p className="text-text-light">
              Il trattamento dei dati sarà svolto in forma automatizzata e manuale, ad opera di soggetti di ciò 
              appositamente incaricati; a tal fine gli strumenti utilizzati e le modalità del trattamento saranno 
              idonei a garantire la sicurezza e la riservatezza dei dati.
            </p>
          </motion.div>

          {/* Consenso Immagini */}
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="bg-surface rounded-xl p-8 shadow-card border border-border mb-8"
          >
            <div className="flex items-center gap-3 mb-4">
              <UserCheck className="w-6 h-6 text-primary" />
              <h3 className="text-lg font-semibold text-text">Consenso per Immagini e Video</h3>
            </div>
            <p className="text-text-light">
              Si acconsente al trattamento e alla pubblicazione, per i soli fini istituzionali, di video, fotografie 
              e/o immagini atte a rivelare l'identità del sottoscritto / del minore, sul sito web e sulle pagine 
              social dell'Associazione.
            </p>
          </motion.div>

          {/* Diritti dell'Interessato */}
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="bg-surface rounded-xl p-8 shadow-card border border-border mb-8"
          >
            <div className="flex items-center gap-3 mb-6">
              <FileEdit className="w-6 h-6 text-primary" />
              <h3 className="text-lg font-semibold text-text">Diritti dell'Interessato (Art. 7)</h3>
            </div>
            <p className="text-text-light mb-6">
              In relazione ai predetti trattamenti Lei potrà esercitare i diritti di cui all'art. 7 del decreto 
              legislativo citato, in particolare potrà ottenere, a cura del titolare o del responsabile della 
              banca dati senza ritardo:
            </p>
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-2 gap-4"
            >
              {dirittiPrivacy.map((diritto, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="bg-muted rounded-lg p-4"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <diritto.icon className="w-5 h-5 text-primary" />
                    </div>
                    <h4 className="font-semibold text-text">{diritto.title}</h4>
                  </div>
                  <p className="text-sm text-text-light">{diritto.description}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Titolare del Trattamento */}
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="bg-primary/5 rounded-xl p-8 border border-primary/20"
          >
            <div className="flex items-center gap-3 mb-4">
              <Lock className="w-6 h-6 text-primary" />
              <h3 className="text-lg font-semibold text-text">Titolare e Responsabile del Trattamento</h3>
            </div>
            <div className="space-y-2 text-text-light">
              <p>
                <strong>Titolare del trattamento:</strong> ASD 99 Castelli, con sede in San Marco di Preturo
              </p>
              <p>
                <strong>Responsabile del trattamento:</strong> Alessandro Serreli, in qualità di Presidente della ASD 99 Castelli
              </p>
            </div>
          </motion.div>
        </div>
      </Section>

      {/* CTA Section */}
      <Section background="primary" className="text-center">
        <motion.div
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="max-w-2xl mx-auto"
        >
          <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-4">
            Hai domande?
          </h2>
          <p className="text-lg text-white/80 mb-8">
            Per qualsiasi chiarimento su Statuto, Condizioni o Privacy, non esitare a contattarci.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="secondary"
              size="lg"
              onClick={() => window.location.href = "mailto:99castelli@gmail.com"}
            >
              Contattaci via Email
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => window.location.href = "/tesseramento"}
              className="border-white text-white hover:bg-white hover:text-primary"
            >
              Vai al Tesseramento
            </Button>
          </div>
        </motion.div>
      </Section>
    </PageLayout>
  );
};

export default Trasparenza;
